import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { useId } from 'react'

export function SparkleRing({
  size,
  count,
  intensity = 1,
}: {
  size: number
  count: number
  intensity?: number
}) {
  const id = useId().replace(/:/g, '')
  const particles = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const angle = (i / count) * 360 + ((i * 17) % 360)
      const radius = size * 0.4 + Math.sin(i) * size * 0.04
      return { angle, radius, delay: (i * 0.27) % 2.5, dur: 1.8 + ((i * 0.3) % 1.5) }
    })
  }, [count, size])
  const r = size * 0.45
  const center = size / 2

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="pointer-events-none absolute inset-0 size-full"
      aria-hidden="true"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <radialGradient id={`spark-${id}`} cx="50%" cy="50%">
          <stop offset="0%" stopColor="var(--brand-peach)" stopOpacity="1" />
          <stop offset="60%" stopColor="var(--brand-accent)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="var(--brand-accent)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {particles.map((p, i) => {
        const x = center + Math.cos((p.angle * Math.PI) / 180) * r
        const y = center + Math.sin((p.angle * Math.PI) / 180) * r
        return (
          <motion.circle
            key={`${id}-s${i}`}
            cx={x}
            cy={y}
            r={1.2 + intensity * 1.4}
            fill={`url(#spark-${id})`}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{
              opacity: [0, 0.95 * intensity, 0],
              scale: [0.5, 1.3, 0.5],
            }}
            transition={{
              duration: p.dur,
              delay: p.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )
      })}
    </svg>
  )
}

export function FireLayer({ size, intensity = 1 }: { size: number; intensity?: number }) {
  const id = useId().replace(/:/g, '')
  const count = Math.round(6 + intensity * 6)
  const flames = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        x: 0.18 + (i / count) * 0.64,
        delay: (i * 0.18) % 1.6,
        dur: 1.3 + ((i * 0.15) % 0.8),
      })),
    [count],
  )

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="pointer-events-none absolute inset-0 size-full"
      aria-hidden="true"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={`fire-${id}`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#ffb45b" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#ff5bc8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#9e5bff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {flames.map((f, i) => (
        <motion.ellipse
          key={`${id}-f${i}`}
          cx={f.x * size}
          cy={size * 0.92}
          rx={size * 0.045}
          ry={size * 0.12}
          fill={`url(#fire-${id})`}
          initial={{ y: 0, opacity: 0, scaleY: 0.6 }}
          animate={{
            y: [-size * 0.05, -size * 0.55 * intensity],
            opacity: [0, 0.9, 0],
            scaleY: [0.7, 1.4, 0.5],
            scaleX: [1, 0.6, 0.3],
          }}
          transition={{
            duration: f.dur,
            delay: f.delay,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      ))}
    </svg>
  )
}

export function LightningLayer({ size, intensity = 1 }: { size: number; intensity?: number }) {
  const id = useId().replace(/:/g, '')
  const strikes = useMemo(
    () =>
      Array.from({ length: 4 }).map((_, i) => {
        const angle = (i / 4) * 360 + ((i * 23) % 360)
        const cx = size / 2
        const cy = size / 2
        const r1 = size * 0.5
        const r2 = size * 0.65
        const x1 = cx + Math.cos((angle * Math.PI) / 180) * r1
        const y1 = cy + Math.sin((angle * Math.PI) / 180) * r1
        const x2 = cx + Math.cos((angle * Math.PI) / 180) * r2
        const y2 = cy + Math.sin((angle * Math.PI) / 180) * r2
        const segs = [
          [x1, y1],
          [x1 + (x2 - x1) * 0.33 + (i % 2 ? 4 : -4), y1 + (y2 - y1) * 0.33 + (i % 2 ? -4 : 4)],
          [x1 + (x2 - x1) * 0.66 + (i % 2 ? -3 : 3), y1 + (y2 - y1) * 0.66 + (i % 2 ? 3 : -3)],
          [x2, y2],
        ]
        return {
          d: segs.map(([x, y], idx) => `${idx === 0 ? 'M' : 'L'}${x},${y}`).join(' '),
          delay: 1.4 + i * 0.6,
          dur: 3.5 + i * 0.4,
        }
      }),
    [size],
  )

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="pointer-events-none absolute inset-0 size-full"
      aria-hidden="true"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <filter id={`glow-${id}`}>
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {strikes.map((s, i) => (
        <motion.path
          key={`${id}-bolt${i}`}
          d={s.d}
          stroke="#00e5ff"
          strokeWidth={1.6 + intensity * 0.6}
          strokeLinecap="round"
          fill="none"
          filter={`url(#glow-${id})`}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, intensity * 0.95, 0, 0, 0] }}
          transition={{
            duration: s.dur,
            delay: s.delay,
            repeat: Infinity,
            times: [0, 0.05, 0.12, 0.4, 1],
            ease: 'easeOut',
          }}
        />
      ))}
    </svg>
  )
}

export function FireworksLayer({ size, intensity = 1 }: { size: number; intensity?: number }) {
  const id = useId().replace(/:/g, '')
  const bursts = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, b) => ({
        cx: 0.18 + ((b * 0.32) % 0.65),
        cy: 0.18 + ((b * 0.4) % 0.55),
        delay: 1.5 + b * 1.8,
        repeatDelay: 4.5,
        rays: Array.from({ length: 10 }).map((__, j) => {
          const a = (j / 10) * 360 + b * 17
          return {
            x: Math.cos((a * Math.PI) / 180),
            y: Math.sin((a * Math.PI) / 180),
            colorIdx: j % 5,
          }
        }),
      })),
    [],
  )
  const palette = ['#0167f4', '#5b6bff', '#9e5bff', '#ff5bc8', '#ffb45b']

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="pointer-events-none absolute inset-0 size-full"
      aria-hidden="true"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <filter id={`fw-glow-${id}`}>
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {bursts.map((burst, b) => {
        const cx = burst.cx * size
        const cy = burst.cy * size
        const reach = size * 0.42 * intensity
        return (
          <g key={`${id}-burst${b}`} filter={`url(#fw-glow-${id})`}>
            {burst.rays.map((ray, j) => {
              const ex = cx + ray.x * reach
              const ey = cy + ray.y * reach
              return (
                <motion.circle
                  key={`${id}-burst${b}-r${j}`}
                  cx={cx}
                  cy={cy}
                  r={1.1}
                  fill={palette[ray.colorIdx]}
                  initial={{ opacity: 0, cx, cy }}
                  animate={{
                    opacity: [0, 1, 0],
                    cx: [cx, ex],
                    cy: [cy, ey],
                  }}
                  transition={{
                    duration: 1.4,
                    delay: burst.delay,
                    repeat: Infinity,
                    repeatDelay: burst.repeatDelay,
                    ease: 'easeOut',
                  }}
                />
              )
            })}
          </g>
        )
      })}
    </svg>
  )
}

export function GradientHaloLayer({ size, intensity = 1 }: { size: number; intensity?: number }) {
  void size
  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute inset-[-15%] rounded-full"
      style={{
        background:
          'conic-gradient(from 0deg, #0167f4, #5b6bff, #9e5bff, #ff5bc8, #ffb45b, #ff5bc8, #0167f4)',
        opacity: 0.18 * intensity,
        filter: 'blur(14px)',
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 16 - intensity * 6, repeat: Infinity, ease: 'linear' }}
    />
  )
}
